import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { navigate } from 'gatsby';
import './login.css';
import './loginLoader.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { faLightbulb as faLightbulbRegular } from '@fortawesome/free-regular-svg-icons';
import { FcGoogle } from 'react-icons/fc';

const Login = () => {
  const [isDark, setIsDark] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [email, setEmail] = useState('');
  const { user, signIn, signInWithGoogle, confirmSignIn, requestPasswordReset, confirmPasswordReset, error, setError } = useAuth();
  const [isLoading, setLoading] = useState(false);
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [confirmResetPassword, setConfirmResetPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (user && user.isSignedIn) {
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    }
  }, [user]);


  const validateEmail = email => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(String(email).toLowerCase())) {
      setEmailError('Enter a valid email id');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = password => {
    if (password.length < 6) {
      setPasswordError('Password is incorrect');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateResetPassword = (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*]/.test(password),
    };
    return Object.values(requirements).every(Boolean);
  };

  const handleSendResetCode = async (event) => {
    event.preventDefault();
    setEmailError('');
    setError('');

    if (!validateEmail(email)) {
      return;
    }

    try {
      setLoading(true);
      const result = await requestPasswordReset(email);

      if (result.success) {
        setCodeSent(true);
      } else {
        const err = result.error;
        if (err.name === 'LimitExceededException') {
          setError('Too many attempts. Please try again in 15 minutes.');
        } else {
          setError('If an account exists, a reset code will be sent shortly.');
          setTimeout(() => setCodeSent(true), 2000);
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setError('');

    if (resetPassword !== confirmResetPassword) {
      setError("Passwords don't match");
      return;
    }

    if (!validateResetPassword(resetPassword)) {
      setError("Password must be at least 8 characters with uppercase, lowercase, number, and special character");
      return;
    }

    try {
      setLoading(true);
      const result = await confirmPasswordReset(email, resetCode, resetPassword);

      if (result.success) {
        setSuccessMessage('Password reset successful! You can now log in.');
        setTimeout(() => {
          setSuccessMessage('');
          setIsForgotPassword(false);
          setCodeSent(false);
          setResetCode('');
          setResetPassword('');
          setConfirmResetPassword('');
          setEmail('');
          setEmailError('');
          setPasswordError('');
          setError('');
        }, 3000);
      } else {
        const err = result.error;
        switch(err.name) {
          case 'ExpiredCodeException':
            setError('Reset code expired. Please request a new one.');
            break;
          case 'CodeMismatchException':
            setError('Incorrect code. Please try again.');
            break;
          case 'InvalidPasswordException':
            setError('Password does not meet requirements.');
            break;
          case 'LimitExceededException':
            setError('Too many attempts. Please try again later.');
            break;
          default:
            setError('Unable to reset password. Please try again.');
        }
      }
    } catch (err) {
      setError('Unable to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async event => {
    event.preventDefault();
    setEmailError('');
    setPasswordError('');
    setError('');
    const isUsernameValid = validateEmail(username);
    const isPasswordValid = validatePassword(password);
    if (!isUsernameValid || !isPasswordValid) {
      return;
    }
    try {
      setLoading(true);
      if (!user || !user.nextStep) {
        await signIn(username, password);
      } else if (
        user &&
        (user.nextStep?.signInStep ===
          'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' ||
          user.nextStep?.signInStep === 'CONFIRM_SIGN_UP')
      ) {
        if (newPassword !== confirmNewPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        await confirmSignIn(newPassword);
      }
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleRequestAccess = async event => {
    event.preventDefault();
    setEmailError('');
    setError('');
    if (!validateEmail(email)) {
      return;
    }
    try {
      setLoading(true);
      setSuccessMessage('');
      const response = await fetch(
        'https://o874r70s6h.execute-api.ap-south-1.amazonaws.com/request_access',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(
          'Thank you for your request. We will get back to you.'
        );
      } else if (response.status === 409) {
        setError('User already exists. Try to log in.');
      } else if (response.status === 500) {
        setError('User already exists. Try to log in.');
      } else {
        setError('An error occurred while requesting access.');
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
      setIsRequestingAccess(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Enter') {
        if (isForgotPassword) {
          if (codeSent) {
            handleResetPassword(event);
          } else {
            handleSendResetCode(event);
          }
        } else if (isRequestingAccess) {
          handleRequestAccess(event);
        } else {
          handleLogin(event);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    username,
    password,
    newPassword,
    confirmNewPassword,
    email,
    isRequestingAccess,
    isForgotPassword,
    codeSent,
    resetCode,
    resetPassword,
    confirmResetPassword,
  ]);

  // Show loader while checking authentication status
  if (user && user.state === 'NOT_FETCHED_YET') {
    return (
      <div className={`login-container${isDark ? '' : ' login-light'}`}>
        <button className="theme-toggle" onClick={() => setIsDark(d => !d)} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
          <FontAwesomeIcon icon={isDark ? faLightbulb : faLightbulbRegular} />
        </button>
        <div className="form">
          <div className="form_front">
            <img
              src="../../images/EngageLogoUpdated.png"
              className="logo"
              alt="logo"
            />
            <div className="loader"></div>
          </div>
        </div>
      </div>
    );
  }

  // If user is already authenticated, don't render login form
  if (user && user.isSignedIn) {
    return null;
  }

  const renderLoader = () => <div className="loader"></div>;

  const renderLoginForm = () => (
    <>
      {user &&
      (user.nextStep?.signInStep ===
        'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' ||
        user.nextStep?.signInStep === 'CONFIRM_SIGN_UP') ? (
        <>
          <input
            type="password"
            className={`input ${newPassword !== confirmNewPassword && newPassword ? 'input-error' : ''}`}
            placeholder="New Password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
          <input
            type="password"
            className={`input ${newPassword !== confirmNewPassword && confirmNewPassword ? 'input-error' : ''}`}
            placeholder="Confirm New Password"
            value={confirmNewPassword}
            onChange={e => setConfirmNewPassword(e.target.value)}
            autoComplete="new-password"
          />
        </>
      ) : (
        <>
          <input
            type="text"
            className={`input ${emailError ? 'input-error' : ''}`}
            placeholder="Username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoComplete="username"
          />
          {emailError && <p className="error">{emailError}</p>}
          <input
            type="password"
            className={`input ${passwordError ? 'input-error' : ''}`}
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
          />
          {passwordError && <p className="error">{passwordError}</p>}
          <button
            type="button"
            className="forgot-password-link"
            onClick={() => {
              setIsForgotPassword(true);
              setEmailError('');
              setPasswordError('');
              setError('');
            }}
          >
            Forgot Password?
          </button>
        </>
      )}

      <button className="login-btn" onClick={handleLogin}>
        Log In
      </button>
      {error && <p className="error">{error}</p>}

      <div className="social-section">
        <div className="divider">
          <span className="divider-text">or</span>
        </div>
        <button className="google-btn" type="button" onClick={signInWithGoogle}>
          <FcGoogle size={20} />
          <span>Sign in with Google</span>
        </button>
      </div>

      <div className="request-access-footer">
        <span className="noAccessText">Don't have login details?</span>
        <button
          className="requestAccess"
          type="button"
          onClick={() => setIsRequestingAccess(true)}
        >
          Request Access
        </button>
      </div>
    </>
  );

  const renderRequestAccessForm = () => (
    <>
      <input
        type="email"
        className={`input emailId ${emailError ? 'input-error' : ''}`}
        placeholder="Enter your email address"
        value={email}
        onChange={e => {
          setEmail(e.target.value);
          setEmailError('');
        }}
      />
      {emailError && <p className="error">{emailError}</p>}
      <button
        className="requestAccess-btn-2 requestAccess-inside"
        type="button"
        onClick={handleRequestAccess}
      >
        Request Access
      </button>
      {error && <p className="error">{error}</p>}
    </>
  );

  const renderSuccessMessage = () => (
    <div style={{ textAlign: 'center' }}>
      <FontAwesomeIcon icon={faThumbsUp} size="3x" color="grey" />
      <p className="thankyouHeading">Thank you for reaching out.</p>
      <span className="thankyouSubtle">
        We'll share the login instructions and the next steps on the email you
        provided.
      </span>
    </div>
  );

  const renderForgotPasswordForm = () => (
    <>
      <input
        autoFocus
        type="email"
        className={`input ${emailError ? 'input-error' : ''}`}
        placeholder="Enter your email"
        value={email}
        onChange={e => {
          setEmail(e.target.value);
          setEmailError('');
        }}
        autoComplete="email"
      />
      {emailError && <p className="error">{emailError}</p>}
      <button
        className="login-btn"
        onClick={handleSendResetCode}
      >
        Send Reset Code
      </button>
      {error && <p className="error">{error}</p>}
      <button
        type="button"
        className="forgot-password-link"
        style={{ marginTop: '10px' }}
        onClick={() => {
          setIsForgotPassword(false);
          setEmailError('');
          setPasswordError('');
          setError('');
          setEmail('');
        }}
      >
        Back to Login
      </button>
    </>
  );

  const renderResetPasswordForm = () => (
    <>
      <input
        autoFocus
        type="text"
        className="input"
        placeholder="Enter 6-digit code"
        value={resetCode}
        onChange={e => setResetCode(e.target.value)}
        maxLength="6"
        inputMode="numeric"
      />
      <input
        type="password"
        className="input"
        placeholder="New password"
        value={resetPassword}
        onChange={e => setResetPassword(e.target.value)}
        autoComplete="new-password"
      />
      <input
        type="password"
        className="input"
        placeholder="Confirm new password"
        value={confirmResetPassword}
        onChange={e => setConfirmResetPassword(e.target.value)}
        autoComplete="new-password"
      />
      <button
        className="login-btn"
        onClick={handleResetPassword}
      >
        Reset Password
      </button>
      {error && <p className="error">{error}</p>}
      <button
        type="button"
        className="forgot-password-link"
        style={{ marginTop: '10px' }}
        onClick={handleSendResetCode}
      >
        Resend Code
      </button>
    </>
  );

  return (
    <div className={`login-container${isDark ? '' : ' login-light'}`}>
      <button className="theme-toggle" onClick={() => setIsDark(d => !d)} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
        <FontAwesomeIcon icon={isDark ? faLightbulb : faLightbulbRegular} />
      </button>
      <div className="form">
        <div className="form_front">
          <img
            src="../../images/EngageLogoUpdated.png"
            className="logo"
            alt="logo"
          />
          <div className="form_details">
            {successMessage
              ? null
              : isForgotPassword
              ? codeSent
                ? 'Reset Your Password'
                : 'Forgot Password'
              : isRequestingAccess
              ? 'Enter your email address'
              : 'Log in to your account!'}
          </div>
          {isLoading ? (
            renderLoader()
          ) : successMessage ? (
            renderSuccessMessage()
          ) : isForgotPassword ? (
            codeSent ? renderResetPasswordForm() : renderForgotPasswordForm()
          ) : (
            <form
              onSubmit={isRequestingAccess ? handleRequestAccess : handleLogin}
            >
              {isRequestingAccess
                ? renderRequestAccessForm()
                : renderLoginForm()}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
